import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  KsAmountCheck,
  KsCommitteeAnswerPayload,
  CaptureKsPayload,
  CreateKsPayload,
  KsAccountConfirmation,
  KsAccountConfirmationAnswer,
  KsApplicationDetail,
  KsApplicationOps,
  KsClientContextEntry,
  KsNamedAccount,
  KsApplicationsResponse,
  KsOpsApplicationsResponse,
  KsAssessResponse,
  KsDecisionPayload,
  KsDocument,
  KsEarlySettlementOverridePayload,
  KsFailedDebitElectionPayload,
  KsFunderAnswerPayload,
  KsFunderRecordPayload,
  KsActivatePayload,
  KsBeforePayoutPanel,
  KsDebitAccountCheckPayload,
  KsDebitOrderAccountPayload,
  KsPayoutDecisionPayload,
  KsRecordRepaymentPayload,
  KsWaivePayload,
  KsCadenceEvidence,
  KsCadenceReferralPayload,
  KsOfferActionPayload,
  KsSubmitResponse,
  KsTaxPin,
} from './kickstart.types';
import {
  KsReceiptDecisionPayload,
  KsReceiptTask,
  KsSigningAct,
  KsSigningPanel,
  KsSubmittedActPayload,
  KsSubmittedPanel,
} from './kickstart-ops.types';
import { OpsEmailLogEntry } from '../status/email-log.types';
import { SizingHoldReleasePayload } from '../applications/sizing-hold.types';

@Injectable({ providedIn: 'root' })
export class KickstartService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/kickstart`;

  // -------------------------------------------------------------------------
  // Client endpoints
  // -------------------------------------------------------------------------

  listForEntity(entityShortId: string): Observable<KsApplicationsResponse> {
    return this.http.get<KsApplicationsResponse>(`${this.base}/applications/`, {
      params: { entity: entityShortId },
    });
  }

  get(shortId: string): Observable<KsApplicationDetail> {
    return this.http.get<KsApplicationDetail>(`${this.base}/applications/${shortId}/`);
  }

  create(payload: CreateKsPayload): Observable<KsApplicationDetail> {
    return this.http.post<KsApplicationDetail>(`${this.base}/applications/`, payload);
  }

  capture(shortId: string, payload: CaptureKsPayload): Observable<KsApplicationDetail> {
    return this.http.patch<KsApplicationDetail>(`${this.base}/applications/${shortId}/`, payload);
  }

  deleteDraft(shortId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/applications/${shortId}/`);
  }

  uploadDocument(
    shortId: string,
    file: File,
    docType: string,
    label?: string,
  ): Observable<KsDocument> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    if (label) {
      fd.append('label', label);
    }
    return this.http.post<KsDocument>(`${this.base}/applications/${shortId}/documents/`, fd);
  }

  /**
   * Soft-delete a supporting document the applicant uploaded by mistake.
   * Backend allows this only on DRAFT / MORE_INFO_REQUIRED applications.
   */
  deleteDocument(shortId: string, docShortId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/applications/${shortId}/documents/${docShortId}/`);
  }

  /**
   * Submit a DRAFT (DOC-72 UI-DA-72-05/06). Sends no consent: the business's
   * AI-assessment consent is given by its own act, and submit only reads it.
   */
  submit(shortId: string): Observable<KsSubmitResponse> {
    return this.http.post<KsSubmitResponse>(`${this.base}/applications/${shortId}/submit/`, {});
  }

  /** DOC-72 UI-DA-72-04 — "every business bank account uploaded?" (FLOW-331). */
  confirmAccounts(
    shortId: string,
    answer: KsAccountConfirmationAnswer,
    accounts: KsNamedAccount[] = [],
  ): Observable<KsAccountConfirmation> {
    return this.http.post<KsAccountConfirmation>(
      `${this.base}/applications/${shortId}/account-confirmation/`,
      { answer, accounts },
    );
  }

  /** DOC-83 §2 — *Send PIN*: the SARS Tax Compliance Status PIN. Every send appends. */
  sendTaxPin(shortId: string, pin: string): Observable<KsTaxPin> {
    return this.http.post<KsTaxPin>(`${this.base}/applications/${shortId}/tax-pin/`, { pin });
  }

  /** DOC-72 UI-DA-72-08 — append one *Tell us about your business* entry. */
  addContext(
    shortId: string,
    entry: { text?: string; file?: File },
  ): Observable<KsClientContextEntry> {
    const fd = new FormData();
    if (entry.text) fd.append('text', entry.text);
    if (entry.file) fd.append('file', entry.file);
    return this.http.post<KsClientContextEntry>(
      `${this.base}/applications/${shortId}/context/`,
      fd,
    );
  }

  respond(shortId: string, aiProcessingConsent: boolean): Observable<KsSubmitResponse> {
    return this.http.post<KsSubmitResponse>(`${this.base}/applications/${shortId}/respond/`, {
      ai_processing_consent: aiProcessingConsent,
    });
  }

  /**
   * Withdraw the AI-processing consent on this application (FLOW-290, POPIA s11(2)).
   *
   * The act behind the "You can withdraw consent at any time" promise. Not an
   * update of the consent row: the server transitions it and every AI gate
   * reads the result live, so the next assessment is refused immediately.
   * Re-consenting goes back through the existing consent path — it writes a new
   * row rather than reviving this one. A second withdrawal is a 404
   * `NO_ACTIVE_CONSENT`.
   *
   * `reason` is optional and never shown back: the right is unqualified, so the
   * UI does not ask a client to justify it. Ops passes one when keying a
   * withdrawal received by phone or email.
   */
  withdrawAiConsent(shortId: string, reason?: string): Observable<KsApplicationDetail> {
    return this.http.post<KsApplicationDetail>(
      `${this.base}/applications/${shortId}/ai-consent/withdraw/`,
      reason ? { reason } : {},
    );
  }

  // -------------------------------------------------------------------------
  // BRE-200 client confirmations — KS-OB-06/07/08 (FLOW-163)
  // -------------------------------------------------------------------------
  // Each is a gating condition for activation: until the client confirms (or
  // Ops records a waiver), an Ops user cannot activate the application. The
  // POST carries no body — hitting the endpoint IS the confirmation. All three
  // are idempotent server-side, so a double-click is harmless.

  /**
   * KS-OB-07 — the client confirms the debit order mandate.
   *
   * Carries the account the debit order runs on — the bank and the last four
   * (DOC-88 UI-DA-88-01) — and nothing else: the frequency is the offer's
   * (`§B11.1`, FLOW-305), and the server refuses one sent here.
   */
  confirmDebitOrder(
    shortId: string,
    account: KsDebitOrderAccountPayload,
  ): Observable<KsSubmitResponse> {
    return this.http.post<KsSubmitResponse>(
      `${this.base}/applications/${shortId}/confirm-debit-order/`,
      account,
    );
  }

  // -------------------------------------------------------------------------
  // FLOW-303 — the offer (Screen 5.0)
  // -------------------------------------------------------------------------

  /** "Accept offer" — `version` guards against accepting terms not on screen. */
  acceptOffer(shortId: string, version: number): Observable<KsSubmitResponse> {
    return this.http.post<KsSubmitResponse>(`${this.base}/applications/${shortId}/offer/accept/`, {
      version,
    });
  }

  /** "Ask to pay monthly instead" — WEEKLY offers only (`§B11.1g`). */
  requestMonthly(shortId: string, reason: string): Observable<KsSubmitResponse> {
    return this.http.post<KsSubmitResponse>(
      `${this.base}/applications/${shortId}/offer/request-monthly/`,
      { reason },
    );
  }

  /** KS-OB-08 — the client confirms the payout bank account, naming it. */
  confirmPayoutAccount(
    shortId: string,
    account: KsDebitOrderAccountPayload,
  ): Observable<KsSubmitResponse> {
    return this.http.post<KsSubmitResponse>(
      `${this.base}/applications/${shortId}/confirm-payout-account/`,
      account,
    );
  }

  // -------------------------------------------------------------------------
  // Ops endpoints
  // -------------------------------------------------------------------------

  opsListApplications(params?: {
    ks_status?: string;
    application_stage?: string;
  }): Observable<KsOpsApplicationsResponse> {
    let httpParams = new HttpParams();
    if (params?.ks_status) {
      httpParams = httpParams.set('ks_status', params.ks_status);
    }
    if (params?.application_stage) {
      httpParams = httpParams.set('application_stage', params.application_stage);
    }
    return this.http.get<KsOpsApplicationsResponse>(`${this.base}/ops/applications/`, {
      params: httpParams,
    });
  }

  opsGet(shortId: string): Observable<KsApplicationOps> {
    return this.http.get<KsApplicationOps>(`${this.base}/ops/applications/${shortId}/`);
  }

  opsDecision(shortId: string, payload: KsDecisionPayload): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/decision/`,
      payload,
    );
  }

  /**
   * FLOW-351 (DOC-82 §4): what the system says of an amount Ops is about to
   * approve — the limit it breaks, or which `§B7` trigger it meets. The screen
   * asks as Ops types; it does no sum of its own.
   */
  opsAmountCheck(shortId: string, amount: string): Observable<KsAmountCheck> {
    return this.http.get<KsAmountCheck>(`${this.base}/ops/applications/${shortId}/amount-check/`, {
      params: { amount },
    });
  }

  /**
   * FLOW-405 (DOC-82-A §2): the Credit Committee's answer on the open referral
   * — a member's own act; the server refuses anyone else (403 NOT_A_MEMBER).
   */
  opsCommitteeAnswer(
    shortId: string,
    payload: KsCommitteeAnswerPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/committee-answer/`,
      payload,
    );
  }

  /** Change terms · Withdraw offer · Issue fresh offer (DOC-62 §8.3). */
  opsOffer(shortId: string, payload: KsOfferActionPayload): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/offer/`,
      payload,
    );
  }

  /** Activate — a separate act; a 409 carries `outstanding` (DOC-62 §8.4). */
  /** FLOW-368 item 5 — *Before payout* (DOC-87). Ops only; read-only. */
  opsBeforePayout(shortId: string): Observable<KsBeforePayoutPanel> {
    return this.http.get<KsBeforePayoutPanel>(
      `${this.base}/ops/applications/${shortId}/before-payout/`,
    );
  }

  /** `KS-OB-10` — *Confirm which assessed account* (DOC-87-A). Returns the refreshed panel. */
  opsDebitAccountCheck(
    shortId: string,
    payload: KsDebitAccountCheckPayload,
  ): Observable<KsBeforePayoutPanel> {
    return this.http.post<KsBeforePayoutPanel>(
      `${this.base}/ops/applications/${shortId}/debit-account-check/`,
      payload,
    );
  }

  /** The Credit Committee's decision past day 30 (DOC-67 UI-DA-67-04). Returns the panel. */
  opsPayoutClockDecision(
    shortId: string,
    payload: KsPayoutDecisionPayload,
  ): Observable<KsBeforePayoutPanel> {
    return this.http.post<KsBeforePayoutPanel>(
      `${this.base}/ops/applications/${shortId}/payout-clock/decision/`,
      payload,
    );
  }

  /** CapX's written notice, the notice itself attached (UI-DA-67-05). Returns the panel. */
  opsPayoutClockNotice(shortId: string, form: FormData): Observable<KsBeforePayoutPanel> {
    return this.http.post<KsBeforePayoutPanel>(
      `${this.base}/ops/applications/${shortId}/payout-clock/notice/`,
      form,
    );
  }

  /** Record a payment against one instalment (FLOW-124). Returns the Ops detail. */
  opsRecordRepayment(
    shortId: string,
    payload: KsRecordRepaymentPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/repayment/`,
      payload,
    );
  }

  /** The final reconciliation — writes REPAID (`§B11`); the note is required. */
  opsConfirmRepaid(shortId: string, note: string): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/confirm-repaid/`,
      { note },
    );
  }

  /** *Waive* a condition (DOC-87 UI-DA-87-02) — the onboarding waiver record, append-only. */
  opsWaive(payload: KsWaivePayload): Observable<unknown> {
    return this.http.post(`${environment.apiUrl}/api/onboarding/waivers/`, payload);
  }

  opsActivate(shortId: string, payload: KsActivatePayload): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/activate/`,
      payload,
    );
  }

  /** `§B11.1b` outcome and `§B11.1c` evidence for an amount's monthly claim. */
  opsCadence(shortId: string, approvedAmount: string): Observable<KsCadenceEvidence> {
    return this.http.get<KsCadenceEvidence>(`${this.base}/ops/applications/${shortId}/cadence/`, {
      params: new HttpParams().set('approved_amount', approvedAmount),
    });
  }

  /** The `§B11.1d` referral record. */
  opsCadenceReferral(
    shortId: string,
    payload: KsCadenceReferralPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/cadence-referral/`,
      payload,
    );
  }

  /** Ops agrees / does not agree a request to pay monthly (`§B11.1g`). */
  opsAnswerFrequencyRequest(
    shortId: string,
    requestShortId: string,
    payload: { agreed: boolean; reason: string; monthly_debit_day?: number | null },
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/frequency-requests/${requestShortId}/answer/`,
      payload,
    );
  }

  /**
   * BRE-200 KS-OB-02a/02b — Ops records the months of bank-statement coverage
   * it accepts. Ops-only by rule (§2.1 "System + Ops"): there is deliberately
   * no client-side counterpart, because an applicant certifying the coverage
   * that gates its own application is an N4 breach.
   *
   * Writing this does not re-activate the coverage conditions — they are
   * dormant under SR-01 until that is decided separately.
   */
  opsAcceptBankData(shortId: string, months: number): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/accept-bank-data/`,
      { months },
    );
  }

  /**
   * FLOW-213 — the §B4.1a SECURITY record for one detected funder: is the
   * debtors book ceded to it, and was any competing cession released? Writer:
   * any Authorised Ops user. The response is the refreshed Ops record — the
   * engine re-scores over the same payload, so `sizing_state` already reflects it.
   */
  opsRecordFunderSecurity(
    shortId: string,
    payload: KsFunderRecordPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/funder-security/`,
      payload,
    );
  }

  /**
   * FLOW-213 — the §B4.1b TREATMENT record: a use of funds or an affordability
   * line item. Writer: whoever holds authority to approve the loan amount —
   * above ZAR 1,000,000 that is Credit Committee tier; the server answers 403
   * `INSUFFICIENT_AUTHORITY` otherwise.
   */
  opsRecordFunderTreatment(
    shortId: string,
    payload: KsFunderRecordPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/funder-treatment/`,
      payload,
    );
  }

  /**
   * FLOW-313 — record the client's answer to the Ops funder question. Append-only:
   * each call is a new answer above the earlier. Any Authorised Ops user.
   */
  /** FLOW-320 — the receipt task (DOC-65). Ops only. */
  opsReceiptDecisions(shortId: string): Observable<KsReceiptTask> {
    return this.http.get<KsReceiptTask>(
      `${this.base}/ops/applications/${shortId}/receipt-decisions/`,
    );
  }

  opsRecordReceiptDecision(
    shortId: string,
    payload: KsReceiptDecisionPayload,
  ): Observable<KsReceiptTask> {
    return this.http.post<KsReceiptTask>(
      `${this.base}/ops/applications/${shortId}/receipt-decisions/`,
      payload,
    );
  }

  /** FLOW-350 — *What the business submitted* (DOC-81). Ops only. */
  opsSubmitted(shortId: string): Observable<KsSubmittedPanel> {
    return this.http.get<KsSubmittedPanel>(`${this.base}/ops/applications/${shortId}/submitted/`);
  }

  /** Exclude, restore or supersede one item, with a reason. Append-only. */
  opsRecordSubmittedAct(
    shortId: string,
    payload: KsSubmittedActPayload,
  ): Observable<KsSubmittedPanel> {
    return this.http.post<KsSubmittedPanel>(
      `${this.base}/ops/applications/${shortId}/submitted/`,
      payload,
    );
  }

  /** FLOW-367 — the signing panel (DOC-68-B). Ops only. */
  opsSigning(shortId: string): Observable<KsSigningPanel> {
    return this.http.get<KsSigningPanel>(`${this.base}/ops/applications/${shortId}/signing/`);
  }

  /** One signing act — Flow records it; nothing is sent from here. Append-only. */
  opsSigningAct(shortId: string, act: KsSigningAct): Observable<KsSigningPanel> {
    const url = `${this.base}/ops/applications/${shortId}/signing/${act.act}/`;
    if (act.act === 'record') {
      const form = new FormData();
      const p = act.payload;
      form.append('client_signed_on', p.client_signed_on);
      form.append('capx_signed_on', p.capx_signed_on);
      form.append('signatory', p.signatory);
      form.append('first_repayment_date', p.first_repayment_date);
      p.files.forEach((f) => form.append('files', f, f.name));
      return this.http.post<KsSigningPanel>(url, form);
    }
    if (act.act === 'disclosure') {
      const form = new FormData();
      form.append('disclosure_date', act.payload.disclosure_date);
      form.append('file', act.payload.file, act.payload.file.name);
      return this.http.post<KsSigningPanel>(url, form);
    }
    if (act.act === 'surety-scan') {
      const form = new FormData();
      form.append('received_on', act.payload.received_on);
      form.append('file', act.payload.file, act.payload.file.name);
      return this.http.post<KsSigningPanel>(url, form);
    }
    return this.http.post<KsSigningPanel>(url, act.payload);
  }

  /** A file a signing act filed — a signed document or the disclosure. */
  opsSigningFile(shortId: string, fileId: number): Observable<Blob> {
    return this.http.get(`${this.base}/ops/applications/${shortId}/signing/files/${fileId}/`, {
      responseType: 'blob',
    });
  }

  /** The file as submitted; the server records DOWNLOADED, or OPENED with `open`. */
  opsSubmittedFile(shortId: string, itemId: string, open: boolean): Observable<Blob> {
    const params = open ? new HttpParams().set('open', '1') : undefined;
    return this.http.get(`${this.base}/ops/applications/${shortId}/submitted/${itemId}/file/`, {
      params,
      responseType: 'blob',
    });
  }

  opsRecordFunderAnswer(
    shortId: string,
    payload: KsFunderAnswerPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/funder-answer/`,
      payload,
    );
  }

  /**
   * FLOW-288 — record CapX's election on the failed-debit charge (`§B1`).
   *
   * 🚨 **The record is what permits the charge, and nothing in the system raises
   * the fee today** — this is the whole mechanism, not a note beside one. The
   * acting person is taken from the session by the server and is never sent.
   *
   * A correction is another POST: the record is append-only, so every election
   * ever made stays visible. The reply is the refreshed Ops record.
   */
  opsRecordFailedDebitElection(
    shortId: string,
    payload: KsFailedDebitElectionPayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/failed-debit-election/`,
      payload,
    );
  }

  /**
   * FLOW-178 (`§B9.1`) — record CapX's written agreement re-enabling the
   * early-settlement discount for ONE settlement date despite the bar. Senior
   * Ops floor, enforced by the server (403). Append-only; the response is the
   * refreshed Ops record.
   */
  opsRecordEarlySettlementOverride(
    shortId: string,
    payload: KsEarlySettlementOverridePayload,
  ): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/early-settlement-override/`,
      payload,
    );
  }

  /**
   * FLOW-277 — release the declared-account hold on a Kickstart file.
   *
   * ⚠️ **Releasing does not itself produce a figure.** The server re-scores the
   * existing run's own payload (no model call) and re-reads both limbs, so if
   * the account list is still not on file a NEW hold comes back on the very
   * response — correct, not a race. The reply is the refreshed Ops record, so
   * the caller replaces its record rather than re-fetching.
   *
   * `release_note` is required: a release with no recorded reason is an
   * unexplained figure three months later.
   */
  opsReleaseSizingHold(shortId: string, releaseNote: string): Observable<KsApplicationOps> {
    return this.http.post<KsApplicationOps>(
      `${this.base}/ops/applications/${shortId}/sizing-hold/release/`,
      { release_note: releaseNote } satisfies SizingHoldReleasePayload,
    );
  }

  opsAssess(shortId: string): Observable<KsAssessResponse> {
    return this.http.post<KsAssessResponse>(`${this.base}/ops/applications/${shortId}/assess/`, {});
  }

  opsGetEmails(shortId: string): Observable<OpsEmailLogEntry[]> {
    return this.http.get<OpsEmailLogEntry[]>(`${this.base}/ops/applications/${shortId}/emails/`);
  }
}
